import React from 'react';
import { FiRefreshCw, FiHeart, FiEdit, FiTrash, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import QuestionnaireAnswerCard from './QuestionnaireAnswerCard';

/**
 * QuestionnaireQuestionCard Component
 * Displays a questionnaire question with optional edit functionality
 * 
 * @param {Object} question - The question object
 * @param {number} index - Question index in the list
 * @param {boolean} showAnswers - Whether to show answer section
 * @param {function} onRegenerateQuestion - Handler for regenerate button
 * @param {function} onLikeQuestion - Handler for like button
 * @param {function} onEditQuestion - Optional handler for edit button
 * @param {function} onDeleteQuestion - Optional handler for delete button
 * @param {function} onMoveUp - Optional handler for move up button
 * @param {function} onMoveDown - Optional handler for move down button
 * @param {number} totalQuestions - Total number of questions
 * @param {string} questionnaireId - Optional questionnaire ID for edit mode
 */
const QuestionnaireQuestionCard = ({
  question,
  index,
  showAnswers = false,
  onRegenerateQuestion,
  onLikeQuestion,
  onEditQuestion = null,
  onDeleteQuestion = null,
  onMoveUp = null,
  onMoveDown = null,
  totalQuestions = 0,
  questionnaireId = null
}) => {
  
  // Render type and skills badges together
  const renderTypeAndSkillsBadges = () => {
    const typeLabels = {
      'multiple-answer': 'Multiple Choice',
      'single-answer': 'Single Choice',
      'long-text': 'Long Text',
      'short-text': 'Short Text',
      'audio-answer': 'Audio',
      'video-answer': 'Video'
    };
    
    const skills = question.inRelationTo;
    let skillsArray = [];
    
    if (skills) {
      skillsArray = Array.isArray(skills) ? skills : [];
    }
    
    return (
      <div className="flex items-center flex-wrap gap-2" style={{ minHeight: '32px' }}>
        {/* Type badge */}
        <span className="px-2 py-0.5 text-md font-regular bg-purple-100 text-purple-800 rounded-full text-center">
          {typeLabels[question.type] || question.type}
        </span>
        
        {/* Input badge */}
        {question.userInput && (
          <span className="px-2 py-0.5 text-md font-regular bg-blue-100 text-blue-800 rounded-full text-center">
            {question.userInput}
          </span>
        )}
        
        {/* Skills pills */}
        {skillsArray.length > 0 && (
          <>
            <span className="text-md text-black">about</span>
            {skillsArray.map((skill, idx) => (
              <span 
                key={idx}
                className="px-2 py-0.5 bg-white border border-blue-500 text-blue-700 rounded-full text-md font-regular"
              >
                {skill}
              </span>
            ))}
          </>
        )}
      </div>
    );
  };

  // Render type-specific content (options for choice questions)
  const renderTypeSpecificContent = () => {
    const type = question.type;
    
    // Choice questions: multiple-answer, single-answer
    if (['multiple-answer', 'single-answer'].includes(type)) {
      return (
        <div className="mt-3 flex flex-row flex-wrap gap-2">
          {question.options?.map((option, idx) => (
            <div key={idx} className="px-4 py-2 bg-gray-200 rounded-full text-gray-700">
              {option}
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="group relative mb-6 bg-white p-5 rounded-lg shadow-sm border border-gray-200 border-t-8 border-t-gray-300 transition-all overflow-hidden">
      <div className="flex gap-4">
        {/* Number circle with move arrows - fixed width */}
        <div className="flex-shrink-0 flex flex-col items-center gap-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#33C6B6' }}>
            <span className="text-white text-md font-regular">{index + 1}</span>
          </div>
          
          {/* Move arrows - appear on hover if not in showAnswers mode */}
          {!showAnswers && questionnaireId && onMoveUp && onMoveDown && (
            <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
              {/* Move Up button */}
              <button
                onClick={() => onMoveUp(index)}
                disabled={index === 0}
                className={`p-1 rounded transition-colors ${
                  index === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700 text-white hover:bg-gray-800'
                }`}
                title={index === 0 ? 'First question' : 'Move up'}
              >
                <FiChevronUp size={14} />
              </button>
              
              {/* Move Down button */}
              <button
                onClick={() => onMoveDown(index)}
                disabled={index === totalQuestions - 1}
                className={`p-1 rounded transition-colors ${
                  index === totalQuestions - 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700 text-white hover:bg-gray-800'
                }`}
                title={index === totalQuestions - 1 ? 'Last question' : 'Move down'}
              >
                <FiChevronDown size={14} />
              </button>
            </div>
          )}
        </div>
        
        {/* Content area - aligned with circle */}
        <div className="flex-1 min-w-0">
          {/* First row with badges and action buttons */}
          <div className="flex items-center justify-between mb-4" style={{ minHeight: '32px' }}>
            <div className="flex items-center flex-wrap gap-2">
              {renderTypeAndSkillsBadges()}
            </div>
            
            {/* Action buttons - slide in from right on hover */}
            {!showAnswers && (
              <div className="flex items-center gap-2 translate-x-full group-hover:translate-x-0 opacity-0 group-hover:opacity-100 transition-all duration-150 ease-out" style={{ height: '32px' }}>
                {/* Regenerate button */}
                <button
                  onClick={() => onRegenerateQuestion(question.id)}
                  className="bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition-all"
                  title="Regenerate question"
                >
                  <FiRefreshCw size={18} />
                </button>
                {/* Delete button */}
                {questionnaireId && onDeleteQuestion && (
                  <button
                    onClick={() => onDeleteQuestion(question, index)}
                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all"
                    title="Delete question"
                  >
                    <FiTrash size={18} />
                  </button>
                )}
                {/* Edit button */}
                {questionnaireId && onEditQuestion && (
                  <button
                    onClick={() => onEditQuestion(question, index)}
                    className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-all"
                    title="Edit question"
                  >
                    <FiEdit size={18} />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Question Header */}
          <div className="mb-2 mt-2">
            {/* Question text */}
            <div className="mb-3">
              <h3 className="text-xl font-medium text-gray-900">
                {question.questionText}
              </h3>
            </div>
            
            {/* Question details - only show if exists */}
            {question.questionDetails && question.questionDetails.trim() && (
              <p className="text-xl text-gray-600 mt-2">
                {question.questionDetails}
              </p>
            )}
          </div>
          
          {/* Type-specific content */}
          {renderTypeSpecificContent()}
          
          {/* Max length */}
          {question.maxLength && (
            <p className="text-sm text-gray-500 mt-2">
              Max length: {question.maxLength}
            </p>
          )}
          
          {/* Answer section */}
          <QuestionnaireAnswerCard
            question={question}
            showAnswers={showAnswers}
          />
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireQuestionCard;
